import { useLatest } from 'ahooks';
import { difference } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Scrollbars as ReactCustomScrollbars } from 'react-custom-scrollbars';
import type { Editor } from '@meitu/saas-editor-core/es/editor';
import type { EventEmitter } from '@meitu/saas-editor-core/es/framework';
import { isHotKeyPress } from '@meitu/saas-editor-core/es/utils/event-kit';
import { CanvasType, IExtraInfo, ILayerProps, PosterType } from '@meitu/saas-editor-core/es/version-types';
import { NAV_RENDER_STAGE_EVENT, NAV_SCROLL_INIT_EVENT, NAV_SCROLL_STOP_EVENT } from '../../event-name';
import { StageLoading } from '../stage-loading';
import { StageListScroll } from './list-scroll';
import { StageItem } from './stage';

type StageListProps = {
    recordId: string;
    setIsAiCreate: (isAiCreate: boolean) => void;
    setAiRegen: (data: { isAiRegen: boolean; regenIndex?: number }) => void;
    editor: Editor;
    events: EventEmitter;
    posterType: PosterType;
    canvasType: CanvasType;
    checkIsCover: (type: any) => boolean;
    closeAiPPTPrompt: () => void;
    stageExtraInfo?: { [key: string]: any };
    isAiPPT: boolean;
    isAiRegen: boolean;
    initPPT: (stageIndex?: number) => void;
    regenIndex: number;
    transferIdParams: any;
    isFromHuaweiCustom: () => boolean;
    getCurrentScene: () => string;
    isFullscreen: boolean;
    showCreateTip: (tip: string) => void;
    hideCreateTip: () => void;
    transferTemplateToClient: (templateDetail: any, fontList: any) => Promise<any>;
    packageTemplateConf: (
        templateConf: any,
        extraInfo: IExtraInfo,
        init: boolean,
    ) => {
        stages: ILayerProps[][];
        extraInfos: IExtraInfo[];
    };
    fetchPPTApplyItem: (params: any) => Promise<any>;
    fetchPPTCheckItemProcess: (params: any) => Promise<any>;
    fetchPPTItemDetail: (params: any) => Promise<any>;
    templateSubject: string;
    fetchAIPPTComponentList: (data: any) => Promise<any>;
    getStagePoints: (layers: ILayerProps[], page_type: string) => number;
    replaceComponentKit: (
        componentUrl: string,
        templateUrl: string,
        layers: ILayerProps[],
        fonts: any[],
        page_type: string,
        themeType: string,
    ) => Promise<any[]>;
};
export const StageList = React.memo(
    ({
        recordId,
        setIsAiCreate,
        setAiRegen,
        editor,
        events,
        posterType,
        canvasType,
        checkIsCover,
        closeAiPPTPrompt,
        stageExtraInfo,
        isAiRegen,
        initPPT,
        regenIndex,
        transferIdParams,
        isFromHuaweiCustom,
        getCurrentScene,
        isFullscreen,
        showCreateTip,
        hideCreateTip,
        transferTemplateToClient,
        packageTemplateConf,
        fetchPPTApplyItem,
        fetchPPTCheckItemProcess,
        fetchPPTItemDetail,
        templateSubject,
        fetchAIPPTComponentList,
        getStagePoints,
        replaceComponentKit,
    }: StageListProps) => {
        const [renderStageList, setRenderStageList] = useState<number[]>([]);
        const [scrollBar, setScrollBar] = useState<ReactCustomScrollbars | null>(null);
        const renderStageListRef = useLatest(renderStageList);
        const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
        const [activeIndex, setActiveIndex] = useState<number>(editor.active.getActiveStageIndex());
        const availableHeight = window.screen.height - 150;
        useEffect(() => {
            setRenderStageList([...editor.stage.getRenderStageList()]);
            const handler = editor.stage.onRerenderAll((renderStageList) => {
                setRenderStageList(renderStageList);
            });
            return () => editor.off(handler);
        }, []);

        useEffect(() => {
            // 监听 scrollBar 初始化事件
            const handler = events.on(NAV_SCROLL_INIT_EVENT, (scrollBar: ReactCustomScrollbars) => {
                setScrollBar(scrollBar);
            });
            return () => events.off(handler);
        }, []);

        useEffect(() => {
            // 监听 scrollBar 滚动事件
            if (scrollBar) {
                events.emit(NAV_SCROLL_STOP_EVENT, scrollBar);
            }
        }, [scrollBar, renderStageList]);

        useEffect(() => {
            // 获取当前可视区域的 stage 索引
            function getVisibleStageIndices(
                scrollTop: number, // 当前滚动位置
                availableHeight: number, // 容器高度
                itemWidth: number, // 每个 stage 的宽度
                itemHeight: number, // 每个 stage 的高度
                sizeList: { width: number; height: number }[], // stage 的总数
            ): number[] {
                const visibleCount = Math.ceil(availableHeight / itemHeight);
                const totalStages = sizeList.length;
                // 根据累加高度计算当前所在的索引
                let accumulatedHeight = 0;
                let startIndex = 0;

                // 遍历找到第一个累加高度大于scrollTop的索引
                for (let i = 0; i < totalStages; i++) {
                    const currentItemHeight = itemWidth * (sizeList[i].height / sizeList[i].width) + 30;
                    accumulatedHeight += currentItemHeight;
                    if (accumulatedHeight > scrollTop) {
                        startIndex = i;
                        break;
                    }
                }
                // 如果从当前 startIndex 开始显示的 stage 数量不足以填满视口，
                // 则向前调整 startIndex，确保滚动到底部时视口始终满屏
                if (startIndex + visibleCount > totalStages) {
                    startIndex = Math.max(totalStages - visibleCount, 0);
                }
                const endIndex = Math.min(startIndex + visibleCount - 1, totalStages - 1);
                // 收集所有可见的 stage 索引
                const visibleIndices: number[] = [];
                for (let i = startIndex; i <= endIndex; i++) {
                    visibleIndices.push(i);
                }

                return visibleIndices;
            }
            events.on(NAV_SCROLL_STOP_EVENT, (scrollBar: ReactCustomScrollbars) => {
                /**按舞台列表中，高/宽 比例最小的来计算最多可显示个数 */
                const sizeList = editor.stage.getSizeList();
                if (!sizeList.length) return;
                const list = renderStageListRef.current;
                const minRatioIndex = sizeList.reduce((minIndex, currentSize, currentIndex, array) => {
                    const currentRatio = currentSize.height / currentSize.width;
                    const minRatio = array[minIndex].height / array[minIndex].width;
                    return currentRatio < minRatio ? currentIndex : minIndex;
                }, 0);
                /** 缩略图的宽度固定170 */
                const itemWidth = 170;
                const itemHeight = itemWidth * (sizeList[minRatioIndex].height / sizeList[minRatioIndex].width) + 20; // 20 是间距
                const currentVisible = getVisibleStageIndices(
                    scrollBar.getScrollTop(),
                    availableHeight,
                    itemWidth,
                    itemHeight,
                    sizeList,
                );
                difference(renderStageListRef.current, currentVisible).forEach((val) => {
                    events.emit(NAV_RENDER_STAGE_EVENT, {
                        originIndex: list?.[val],
                        needRender: false,
                    });
                });
                currentVisible.forEach((val) => {
                    events.emit(NAV_RENDER_STAGE_EVENT, {
                        originIndex: list?.[val],
                        needRender: true,
                    });
                });
            });
            return () => {
                events.off(NAV_SCROLL_STOP_EVENT);
            };
        }, []);

        // 监听 stage 选中状态变化事件, 更新 selectedIndexes
        useEffect(() => {
            return editor.stage.onStageSelectionChange((indexes) => {
                setSelectedIndexes(indexes);
            });
        }, []);

        // 监听 z 键按下事件, 下面的 onStageChange 里面需要排除掉快捷键撤销的情况
        const isZPressed = useRef(false);
        useEffect(() => {
            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'z') {
                    isZPressed.current = true;
                }
            };
            const handleKeyUp = (e: KeyboardEvent) => {
                isZPressed.current = false;
            };
            document.addEventListener('keydown', handleKeyDown);
            document.addEventListener('keyup', handleKeyUp);
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
                document.removeEventListener('keyup', handleKeyUp);
            };
        }, []);

        useEffect(() => {
            const handler = editor.active.onStageChange(({ stageIndex }) => {
                setActiveIndex(stageIndex);

                // @TODO 这个设计太绕了, 后续需要寻找更直接的实现方式
                // 正常操作, stage change 的时候需要同时改成选中状态, 但是多选的情况需要排出掉
                // 还需要排除掉用 cmd + z 来撤销的情况
                const hotKeyPressed = isHotKeyPress() && !isZPressed.current;
                if (!hotKeyPressed) {
                    setSelectedIndexes([stageIndex]);
                }
            });
            return () => editor.off(handler);
        }, []);

        // 监听 stage 重新渲染事件, 更新 activeIndex 和 selectedIndexes
        useEffect(() => {
            const handler = editor.stage.onRerenderAll(() => {
                const activeIndex = editor.active.getActiveStageIndex();
                setActiveIndex(activeIndex);
                setSelectedIndexes([activeIndex]);
            });
            return () => editor.off(handler);
        }, []);

        return (
            <>
                <StageListScroll editor={editor} events={events} />
                {renderStageList.map((originIndex, index) => {
                    return (
                        <StageItem
                            key={originIndex}
                            originIndex={originIndex}
                            stageIndex={index}
                            selectedIndexes={selectedIndexes}
                            activeIndex={activeIndex}
                            editor={editor}
                            events={events}
                            isAiRegen={isAiRegen}
                            initPPT={initPPT}
                            regenIndex={regenIndex}
                            transferIdParams={transferIdParams}
                            isFromHuaweiCustom={isFromHuaweiCustom}
                            getCurrentScene={getCurrentScene}
                            isFullscreen={isFullscreen}
                            showCreateTip={showCreateTip}
                            hideCreateTip={hideCreateTip}
                            transferTemplateToClient={transferTemplateToClient}
                            packageTemplateConf={packageTemplateConf}
                            fetchPPTApplyItem={fetchPPTApplyItem}
                            fetchPPTCheckItemProcess={fetchPPTCheckItemProcess}
                            fetchPPTItemDetail={fetchPPTItemDetail}
                            templateSubject={templateSubject}
                            posterType={posterType}
                            canvasType={canvasType}
                            checkIsCover={checkIsCover}
                            closeAiPPTPrompt={closeAiPPTPrompt}
                            stageExtraInfo={stageExtraInfo}
                            recordId={recordId}
                            setIsAiCreate={setIsAiCreate}
                            setAiRegen={setAiRegen}
                            fetchAIPPTComponentList={fetchAIPPTComponentList}
                            getStagePoints={getStagePoints}
                            replaceComponentKit={replaceComponentKit}
                        />
                    );
                })}
                {/* ai ppt 逐页生成 loading */}
                <StageLoading events={events} theme={editor.getTheme()} />
            </>
        );
    },
);
